import type { ObjectId } from 'mongodb';

import type { AccountDocument } from './AccountTypes';
import type { AdderType } from './Adders';
import { ContractStatus } from './Contracts';
import type { Entity } from './Entities';
import type { ExecutionContext } from './ExecutionContext';
import type { IsoDateTimeString } from './Helpers';
import type { PriceSheetMappingDocument } from './PriceSheetMapping';
import { isSolarProgramType, ProgramType } from './ProgramTypes';
import type { SolarQuoteDocument } from './Quotes';
import type {
  ChangeValidationReason,
  ChangeValidationStatus,
  ProductionRangeValidationResult,
  SystemSizeRangeValidationResult,
} from './ValidateChange';

export enum AddendumType {
  epcChangeOrder = 'epcChangeOrder',
}

export enum ChangeOrderStatus {
  created = 'created',
  sent = 'sent',
  viewed = 'viewed',
  partiallyAccepted = 'partiallyAccepted',
  executed = 'executed',
  declined = 'declined',
  expired = 'expired',
  cancelled = 'cancelled',
  voided = 'voided',
  awaitingDisclosure = 'awaitingDisclosure',
}

export interface ChangeOrderInitiatedBy {
  organizationId: string;
  organizationName: string;
  userId: string;
  userName: string;
  email: string;
  ipAddress?: string;
  userAgent?: string;
  submittedAt: IsoDateTimeString;
}

export interface ChangeOrderChangedField {
  field: string;
  fromValue: unknown;
  toValue: unknown;
}

export interface ChangeOrderOfferLink {
  email: string;
  url: string;
}

/** A single human-readable, non-authoritative summary line of a change order change: a localized
 * label plus the formatted previous and new values. Shared by the notification emails/SMS and the
 * web acceptance page so every surface renders identical rows. This is display-only — the
 * authoritative raw data is `changedFields`. */
export interface ChangeOrderSummaryEntry {
  label: string;
  previousValue: string;
  newValue: string;
}

/** A single "staying the same" summary line: a localized label plus the formatted current value of a
 * customer-visible key term the change order left unchanged. Display-only companion to
 * `ChangeOrderSummaryEntry` so a surface can render the "what is changing" and "what is staying the
 * same" tables from the same source. */
export interface ChangeOrderUnchangedEntry {
  label: string;
  value: string;
}

/** change-order `changedFields` whose stored value is an opaque identifier that means nothing to a
 * customer, so it is hidden from all customer-facing surfaces (the review payload's raw
 * `changedFields`, and the formatted `changeSummary` rows). Single source of truth — consumed by
 * both getChangeOrderReview and buildChangeOrderSummary. */
export const CHANGE_ORDER_CUSTOMER_HIDDEN_FIELDS = ['productId'] as const;

export interface ChangeOrderAcceptance {
  email: string;
  acceptedAt: IsoDateTimeString;
  acceptedIpAddress?: string;
  acceptedUserAgent?: string;
}

export interface ChangeOrderDecline {
  email: string;
  declinedAt: IsoDateTimeString;
  ipAddress?: string;
  userAgent?: string;
  /** Mandatory free-text reason the signer gives when declining. */
  reason: string;
}

export enum ChangeOrderReasonGroup {
  systemDesign = 'systemDesign',
  siteConditions = 'siteConditions',
  pricing = 'pricing',
  other = 'other',
}

export enum ChangeOrderReasonCode {
  systemResize = 'systemResize',
  productionAdjustment = 'productionAdjustment',
  batteryAddRemove = 'batteryAddRemove',
  otherAdderChange = 'otherAdderChange',
  roofRedesign = 'roofRedesign',
  shadingObstruction = 'shadingObstruction',
  structuralRequirement = 'structuralRequirement',
  rateEscalatorUpdate = 'rateEscalatorUpdate',
  customerRequest = 'customerRequest',
  permittingRequirement = 'permittingRequirement',
  qcRequest = 'qcRequest',
  other = 'other',
}

export const CHANGE_ORDER_REASON_GROUP_LABELS: Record<ChangeOrderReasonGroup, string> = {
  [ChangeOrderReasonGroup.systemDesign]: 'System design',
  [ChangeOrderReasonGroup.siteConditions]: 'Site conditions',
  [ChangeOrderReasonGroup.pricing]: 'Pricing',
  [ChangeOrderReasonGroup.other]: 'Other',
};

export const CHANGE_ORDER_REASON_LABELS: Record<ChangeOrderReasonCode, string> = {
  [ChangeOrderReasonCode.systemResize]: 'System resize',
  [ChangeOrderReasonCode.productionAdjustment]: 'Production adjustment',
  [ChangeOrderReasonCode.batteryAddRemove]: 'Battery add / remove',
  [ChangeOrderReasonCode.otherAdderChange]: 'Other adder change',
  [ChangeOrderReasonCode.roofRedesign]: 'Roof redesign',
  [ChangeOrderReasonCode.shadingObstruction]: 'Shading / obstruction',
  [ChangeOrderReasonCode.structuralRequirement]: 'Structural requirement',
  [ChangeOrderReasonCode.rateEscalatorUpdate]: 'Rate / escalator update',
  [ChangeOrderReasonCode.customerRequest]: 'Customer request',
  [ChangeOrderReasonCode.permittingRequirement]: 'Permitting requirement',
  [ChangeOrderReasonCode.qcRequest]: 'QC request',
  [ChangeOrderReasonCode.other]: 'Other',
};

export const CHANGE_ORDER_REASON_GROUPS: ReadonlyArray<{
  group: ChangeOrderReasonGroup;
  reasons: ReadonlyArray<ChangeOrderReasonCode>;
}> = [
  {
    group: ChangeOrderReasonGroup.systemDesign,
    reasons: [
      ChangeOrderReasonCode.systemResize,
      ChangeOrderReasonCode.productionAdjustment,
      ChangeOrderReasonCode.batteryAddRemove,
      ChangeOrderReasonCode.otherAdderChange,
    ],
  },
  {
    group: ChangeOrderReasonGroup.siteConditions,
    reasons: [
      ChangeOrderReasonCode.roofRedesign,
      ChangeOrderReasonCode.shadingObstruction,
      ChangeOrderReasonCode.structuralRequirement,
    ],
  },
  {
    group: ChangeOrderReasonGroup.pricing,
    reasons: [ChangeOrderReasonCode.rateEscalatorUpdate],
  },
  {
    group: ChangeOrderReasonGroup.other,
    reasons: [
      ChangeOrderReasonCode.customerRequest,
      ChangeOrderReasonCode.permittingRequirement,
      ChangeOrderReasonCode.qcRequest,
      ChangeOrderReasonCode.other,
    ],
  },
];

/** Snapshot of the locked price-sheet mapping and modifier ids used to validate/recalc this CO, retained for the audit trail. */
export interface ChangeOrderLockedPricingRef {
  priceSheetId?: ObjectId;
  modifierIds?: ObjectId[];
}

export interface ChangeOrderDocument extends Entity {
  accountId: ObjectId;
  /** The never-voided signed contract this addendum chain hangs off. */
  originalContractId: ObjectId;
  /** The quote/addendum this CO diffs against (original or prior CO's quote). */
  baseQuoteId: ObjectId;
  /** The cloned sub-quote, status `changeOrderPending`. */
  proposedQuoteId: ObjectId;
  /** 1-based ordinal within the contract's addendum chain. */
  sequence: number;
  type: AddendumType;
  status: ChangeOrderStatus;
  initiatedBy: ChangeOrderInitiatedBy;
  /** Reason(s) the initiating EPC chose for opening this change order. */
  reasons: ChangeOrderReasonCode[];
  /** Whether the EPC confirmed the change order before submission. Stored for audit; enforcement deferred to FIN-4725. */
  confirmedByEpc?: boolean;
  changedFields: ChangeOrderChangedField[];
  offerLinks: ChangeOrderOfferLink[];
  acceptances: ChangeOrderAcceptance[];
  declines: ChangeOrderDecline[];
  /** References the generated amendment PDF, set on execution (after all signers accept). */
  amendmentDocumentId?: ObjectId;
  lockedPricingRef?: ChangeOrderLockedPricingRef;
  /** The amended Illinois Shines disclosure the customer must sign before the CO opens for acceptance; also used to poll the vendor and to supersede the prior disclosure at execute. */
  illinoisShinesDisclosure?: {
    formId: string;
    sentAt: IsoDateTimeString;
    signedAt?: IsoDateTimeString;
    providerData?: { projectSizekWDC: number; projectSizekWAC: number };
  };
  sentAt?: IsoDateTimeString;
  /** Offer expiry; the per-signer link lifetime is tracked separately. */
  expiresAt?: IsoDateTimeString;
  /** The reminder day (e.g. 1/2/3/5/8/13/21/30) of the most recent reminder sent. */
  lastReminderStep?: number;
  lastReminderSentAt?: IsoDateTimeString;
}

/** Allowed CO lifecycle transitions. Terminal states map to an empty array. */
export const CHANGE_ORDER_STATUS_TRANSITIONS: Readonly<Record<ChangeOrderStatus, ReadonlyArray<ChangeOrderStatus>>> =
  {
    [ChangeOrderStatus.created]: [ChangeOrderStatus.sent, ChangeOrderStatus.cancelled],
    [ChangeOrderStatus.sent]: [
      ChangeOrderStatus.viewed,
      ChangeOrderStatus.partiallyAccepted,
      ChangeOrderStatus.executed,
      ChangeOrderStatus.declined,
      ChangeOrderStatus.expired,
      ChangeOrderStatus.cancelled,
    ],
    [ChangeOrderStatus.viewed]: [
      ChangeOrderStatus.partiallyAccepted,
      ChangeOrderStatus.executed,
      ChangeOrderStatus.declined,
      ChangeOrderStatus.expired,
      ChangeOrderStatus.cancelled,
    ],
    [ChangeOrderStatus.partiallyAccepted]: [
      ChangeOrderStatus.executed,
      ChangeOrderStatus.declined,
      ChangeOrderStatus.expired,
      ChangeOrderStatus.cancelled,
    ],
    [ChangeOrderStatus.executed]: [ChangeOrderStatus.voided],
    [ChangeOrderStatus.declined]: [],
    [ChangeOrderStatus.expired]: [],
    [ChangeOrderStatus.cancelled]: [],
    [ChangeOrderStatus.voided]: [],
    // `expired` is reachable without ever passing through `sent`: a customer who never signs the
    // amended disclosure leaves the CO here, and the daily sweep must be able to retire it.
    [ChangeOrderStatus.awaitingDisclosure]: [
      ChangeOrderStatus.sent,
      ChangeOrderStatus.expired,
      ChangeOrderStatus.cancelled,
    ],
  };

/**
 * Statuses treated as "pending" for duplicate-CO prevention (the partial-unique index) and
 * pending-CO detection. `executed` is intentionally excluded: it is a success state that may
 * still transition to `voided`, but it is not pending.
 */
export const NON_TERMINAL_CHANGE_ORDER_STATUSES: ReadonlyArray<ChangeOrderStatus> = [
  ChangeOrderStatus.created,
  ChangeOrderStatus.sent,
  ChangeOrderStatus.viewed,
  ChangeOrderStatus.partiallyAccepted,
  ChangeOrderStatus.awaitingDisclosure,
];

/** Statuses where a CO can no longer be reviewed/acted on — accept/decline/view are all rejected. */
export const TERMINAL_CHANGE_ORDER_STATUSES: ReadonlyArray<ChangeOrderStatus> = [
  ChangeOrderStatus.executed,
  ChangeOrderStatus.declined,
  ChangeOrderStatus.expired,
  ChangeOrderStatus.cancelled,
  ChangeOrderStatus.voided,
];

export const canTransitionChangeOrderStatus = (from: ChangeOrderStatus, to: ChangeOrderStatus): boolean =>
  CHANGE_ORDER_STATUS_TRANSITIONS[from].includes(to);

/**
 * Statuses whose offer is still live. `expired` is excluded deliberately: expiry is what stops a stale
 * amendment being accepted against pricing that has moved.
 */
export const RESENDABLE_CHANGE_ORDER_STATUSES: ReadonlySet<ChangeOrderStatus> = new Set([
  ChangeOrderStatus.sent,
  ChangeOrderStatus.partiallyAccepted,
]);

export const CANCELLABLE_CHANGE_ORDER_STATUSES: ReadonlyArray<ChangeOrderStatus> = (
  Object.keys(CHANGE_ORDER_STATUS_TRANSITIONS) as ChangeOrderStatus[]
).filter((status) => canTransitionChangeOrderStatus(status, ChangeOrderStatus.cancelled));

/**
 * A partial set of proposed values for the *validate* request — all optional, so a caller can supply
 * just the fields it wants compared (or none, for a pure eligibility check). This is a comparison
 * vocabulary, NOT a contract-mutation vocabulary; the create path uses `ChangeOrderProposal` instead.
 */
export interface ProposedContractChanges {
  /** New financing product; determines kwhRate, escalationRate, and PPW options. */
  productId?: string;
  systemSizeKw?: number;
  systemFirstYearProductionKwh?: number;
  /** Per-kWh rate to validate against the locked bounds; matches the quote-request vocabulary. */
  kwhRate?: number;
  escalationRate?: number;
  adders?: AdderType[];
  battery?: SolarQuoteDocument['batteryDetails'] | null;
  batteryCost?: number;
}

/**
 * The desired end-state for *creating* a change order (internal grouping — the create route accepts
 * these fields flat, alongside reasons/confirmedByEpc). Unlike a patch, absence is meaningful:
 *   - `battery` present → that battery; absent (or null) → no battery (an existing battery is removed).
 *   - `adders` is the complete set; an adder previously present but not sent is removed.
 *
 * The physical system state (systemSizeKw, systemFirstYearProductionKwh) is required. The pricing
 * anchor is optional-inherit: omit `productId` to keep the locked base product. At least one of
 * `productId` or (`kwhRate` + `escalationRate`) must be supplied (enforced at the route). The
 * signing-time pricing snapshot (domesticContentEligible, globalPpwModifier, org*ModifierId) is NOT
 * part of this vocabulary — it stays locked and is inherited from the base quote.
 */
export interface ChangeOrderProposal {
  /** Financing product; omit to keep the locked base product. When supplied, a deliberate change. */
  productId?: string;
  systemSizeKw: number;
  systemFirstYearProductionKwh: number;
  /** Per-kWh rate, validated against the locked bounds; matches the quote-request vocabulary. */
  kwhRate?: number;
  escalationRate?: number;
  /** The complete adder set; an adder previously present but omitted here is removed. */
  adders?: AdderType[];
  /** The full battery state; absent or null means no battery (an existing battery is removed). */
  battery?: SolarQuoteDocument['batteryDetails'] | null;
  /** Battery cost in dollars; used for a rawCost battery. Falls back to the base quote's stored cost. */
  batteryCost?: number;
}

/**
 * The pricing patch returned by the injected recompute function — the subset of quote fields the sub-quote clone overrides.
 * productId, productName, kwhPrice, and escalationRate are included so the orchestrator can populate the clone and build the changedFields diff.
 *
 * Intentionally NOT included (so they remain locked from the base quote via the clone spread, not re-derived at CO time):
 * `domesticContentEligible`, `globalPpwModifier`, and the `org*ModifierId` reference fields — these are the signing-time
 * pricing snapshot. Do not "fix" their absence here; that would unlock pricing that is meant to stay frozen for the contract.
 */
export type ChangeOrderRecomputedPricing = Pick<
  SolarQuoteDocument,
  | 'productId'
  | 'productName'
  | 'kwhPrice'
  | 'escalationRate'
  | 'systemPricingDetails'
  | 'totalAmountPaid'
  | 'totalSystemCost'
  | 'totalSystemCostWithoutDomesticContent'
  | 'pricePerWatt'
  | 'pricePerWattWithoutDomesticContent'
  | 'actualPricePerWatt'
  | 'actualPricePerWattWithoutDomesticContent'
  | 'actualTotalSystemCost'
  | 'actualTotalSystemCostWithoutDomesticContent'
  | 'standardHoldbackAmount'
  | 'domesticContentHoldbackAmount'
  | 'totalHoldbackAmount'
  | 'batteryDetails'
  | 'adders'
  | 'additionalCostAdders'
>;

/** Contract statuses a change order can be opened against */
export const CHANGE_ORDER_ELIGIBLE_CONTRACT_STATUSES: ContractStatus[] = [
  ContractStatus.approved,
  ContractStatus.signed,
];

export const isChangeOrderEligibleContractStatus = (status: ContractStatus): boolean =>
  CHANGE_ORDER_ELIGIBLE_CONTRACT_STATUSES.includes(status);

export const isChangeOrderEligibleContract = (programType: ProgramType, status: ContractStatus): boolean =>
  isSolarProgramType(programType) && isChangeOrderEligibleContractStatus(status);

/**
 * Stable machine-readable code for each change-order ineligibility gate, returned beside the human
 * `ineligibleReason` so integrators can branch on a value rather than parse a string.
 */
export enum ChangeOrderIneligibleCode {
  INVALID_ACCOUNT_ID = 'invalidAccountId',
  ACCOUNT_NOT_FOUND = 'accountNotFound',
  NOT_AUTHORIZED = 'notAuthorized',
  NON_SOLAR_PROGRAM = 'nonSolarProgram',
  NO_ACTIVE_CONTRACT = 'noActiveContract',
  CHANGE_ORDER_PENDING = 'changeOrderPending',
  INSTALL_IN_REVIEW = 'installInReview',
  INSTALL_APPROVED = 'installApproved',
  BILLING_STARTED = 'billingStarted',
  TRANCHE_CLOSED = 'trancheClosed',
  PRICING_LOCK_UNRESOLVED = 'pricingLockUnresolved',
  BASE_QUOTE_NOT_FOUND = 'baseQuoteNotFound',
  KWH_RATE_OUT_OF_BOUNDS = 'kwhRateOutOfBounds',
  ESCALATOR_OUT_OF_BOUNDS = 'escalatorOutOfBounds',
  PENDING_ADJUSTMENT = 'pendingAdjustment',
}

/**
 * Request body for `POST /v3/accounts/:accountId/change-orders/validate` — a flat, partial set of
 * proposed values to compare against the active quote. Send an empty body for a pure eligibility check.
 */
export type ChangeOrderValidateRequestBody = ProposedContractChanges;

/** Response for `POST /v3/accounts/:accountId/change-orders/validate` — unifies "allowed" (eligible) and "required" (changeValidation) in one decision. */
export interface ChangeOrderValidateResponse {
  eligible: boolean;
  ineligibleReason?: string;
  /** Stable code for the failing gate; present whenever `eligible` is false. */
  ineligibleCode?: ChangeOrderIneligibleCode;
  /** Present only when the request supplied at least one comparable value (systemSizeKw, systemFirstYearProductionKwh, or adders). */
  changeValidation?: {
    status: ChangeValidationStatus;
    reasons: ChangeValidationReason[];
    production?: ProductionRangeValidationResult;
    systemSize?: SystemSizeRangeValidationResult;
  };
}

/** Injected pricing function signature shared between server-core (consumer) and api (implementor). */
export type ChangeOrderPricingRecomputer = (
  executionContext: ExecutionContext,
  input: {
    account: AccountDocument<ProgramType.solar>;
    baseQuote: SolarQuoteDocument;
    lockedMapping: PriceSheetMappingDocument<ProgramType.solar>;
    proposal: ChangeOrderProposal;
  },
) => Promise<ChangeOrderRecomputedPricing>;
